/**
 * Entity → EntityView projection used by the network filter (and the harness view).
 * `withDetail` must only be true for the entity's owner.
 */
import { isAircraft, isShip } from './entities.js';
import { TICK_HZ } from './balance.js';
import type { Entity, EntityView, EntityViewState, MatchState, MotionSeg, OwnEntityDetail, ShipEntity, Vec2 } from './types.js';

/** Position on a motion segment at (possibly fractional) tick. */
export function segPos(seg: MotionSeg, tick: number, out: Vec2 = { x: 0, y: 0 }): Vec2 {
  const dt = (tick - seg.tick) / TICK_HZ;
  out.x = seg.x + seg.vx * dt;
  out.y = seg.y + seg.vy * dt;
  return out;
}

export function entityViewState(state: MatchState, e: Entity): EntityViewState {
  const t = state.tick;
  switch (e.kind) {
    case 'city':
      if (e.buildDoneTick > t) return 'building';
      return t < e.braceUntil ? 'braced' : 'active';
    case 'sam':
      if (e.buildDoneTick > t) return 'building';
      return t < e.exposedUntil ? 'exposed' : 'active';
    case 'radar':
    case 'airbase':
      return e.buildDoneTick > t ? 'building' : 'active';
    case 'silo':
      if (e.buildDoneTick > t) return 'building';
      if (t < e.exposedUntil) return 'exposed';
      return e.loaded > 0 ? 'loaded' : 'active';
    case 'sub':
      if (e.buildDoneTick > t) return 'building';
      return t < e.exposedUntil ? 'exposed' : 'active';
    case 'carrier':
      return e.buildDoneTick > t ? 'building' : 'active';
    case 'fighter':
    case 'bomber':
      return e.phase === 'building' ? 'building' : 'active';
    default:
      return 'active';
  }
}

/** Waypoints sent with a ship's owner detail (a long route is drawn up to here; the rest follows as it sails). */
export const ROUTE_MAX = 32;

/** A ship's remaining water route as flat [x, y, …] (owner detail). */
function routeOf(state: MatchState, s: ShipEntity): number[] {
  const w = state.world;
  const out: number[] = [];
  for (let i = 0; i < s.path.length && i < ROUTE_MAX; i++) out.push(w.wcx[s.path[i]], w.wcy[s.path[i]]);
  return out;
}

function extraOf(e: Entity): number {
  switch (e.kind) {
    case 'city':
      return e.level;
    case 'sam':
      return e.ammo;
    case 'carrier':
      return e.hp;
    default:
      return 0;
  }
}

function detailOf(state: MatchState, e: Entity): OwnEntityDetail {
  switch (e.kind) {
    case 'radar':
      return { buildDoneTick: e.buildDoneTick };
    case 'sam':
      return { buildDoneTick: e.buildDoneTick, ammo: e.ammo, reloadAt: e.reloadAt, priorityId: e.priorityId, silent: e.silent, exposedUntil: e.exposedUntil };
    case 'silo':
      return { buildDoneTick: e.buildDoneTick, loaded: e.loaded, queued: e.queued, loadDoneTick: e.loadDoneTick, cooldownUntil: e.cooldownUntil, exposedUntil: e.exposedUntil };
    case 'city':
      return { buildDoneTick: e.buildDoneTick, level: e.level, upgradeDoneTick: e.upgradeDoneTick, braceUntil: e.braceUntil, braceReadyTick: e.braceReadyTick };
    case 'airbase':
      return { buildDoneTick: e.buildDoneTick, autoScramble: e.autoScramble };
    case 'fighter':
    case 'bomber':
      return { baseId: e.baseId, phase: e.phase, mission: e.mission, tx: e.tx, ty: e.ty, readyTick: e.readyTick, ammo: e.ammo, stationUntil: e.stationUntil };
    case 'sub':
      return {
        buildDoneTick: e.buildDoneTick,
        hp: e.hp,
        loaded: e.slbms,
        queued: e.slbmQueued,
        loadDoneTick: e.slbmLoadDoneTick,
        torps: e.torps,
        destX: e.destX,
        destY: e.destY,
        route: routeOf(state, e),
        docked: e.inPort,
        exposedUntil: e.exposedUntil,
      };
    case 'carrier':
      return {
        buildDoneTick: e.buildDoneTick,
        hp: e.hp,
        ammo: e.samAmmo,
        reloadAt: e.samReloadAt,
        destX: e.destX,
        destY: e.destY,
        route: routeOf(state, e),
        autoScramble: e.autoScramble,
      };
    default:
      return {};
  }
}

export function entityView(state: MatchState, e: Entity, withDetail: boolean): EntityView {
  const mobile = isAircraft(e) || isShip(e);
  const v: EntityView = {
    id: e.id,
    kind: e.kind,
    owner: e.owner,
    state: entityViewState(state, e),
    x: mobile ? e.seg.x : e.x,
    y: mobile ? e.seg.y : e.y,
    vx: mobile ? e.seg.vx : 0,
    vy: mobile ? e.seg.vy : 0,
    segTick: mobile ? e.seg.tick : e.createdTick,
    extra: extraOf(e),
  };
  if (withDetail) v.detail = detailOf(state, e);
  return v;
}
